import { FC, SVGProps } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ReactComponent as BackIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as DownloadIcon } from 'src/assets/svg/blue-file-download.svg'
import { ReactComponent as EditIcon } from 'src/assets/svg/edit-pencil-16.svg'
import { PageBackLink } from 'src/components/PageBackLink'
import en from 'src/constants/en'
import { cn } from 'src/utils/cn'
import styles from './campaignDetailHeader.module.scss'

export type CampaignDetailView = 'comparison' | 'general'

// The action that sits beside Download Mentions Report. A campaign's own page
// offers Edit Campaign; the campaigns overview offers Add New Campaign, so the
// button is described by the page rather than fixed here.
export interface CampaignDetailAction {
  icon: FC<SVGProps<SVGSVGElement>>
  label: string
  onClick: () => void
}

interface CampaignDetailHeaderProps {
  // Overrides the Edit Campaign button. Unset, the header behaves as it does on
  // a campaign's own page.
  action?: CampaignDetailAction
  backUrl: string
  campaignName?: string
  className?: string
  // Drops the campaign name to 16px. Used where the header sits on the page
  // background above a widget rather than titling a card of its own.
  compact?: boolean
  // The right segment's label: "Data Comparison" on a campaign, "Reporting &
  // Comparison" on Review Analysis, whose report is a comparison of facilities
  // rather than of periods.
  comparisonLabel?: string
  date?: string
  editUrl?: string
  // The left segment's label: "General Overview" on a campaign, plain
  // "Overview" on the campaigns list, where there is no detail to contrast it
  // with.
  generalLabel?: string
  isDownloading?: boolean
  // Takes over the back chevron. Data Comparison uses it to step back through
  // its own screens — an open report returns to the history list — instead of
  // leaving the campaign altogether.
  onBack?: () => void
  onDownload?: () => void
  onViewChange: (view: CampaignDetailView) => void
  // The section above this page, shown as the shared "← Parent" link over the
  // title (e.g. "Social Listening" over "Campaigns"). `compact` folds the name
  // into the link, since a heading of its own follows.
  parent?: string
  showActions?: boolean
  view: CampaignDetailView
}

// A campaign path reads "Snap Fitness / Brand Mentions"; the comparison
// screens read "Social Listening · Comparisons". Both split at the first
// separator, so the header does not need to know which screen it is on.
const NAME_SEPARATOR = / \/ | · /

const viewOptions = (
  generalLabel: string,
  comparisonLabel: string
): { label: string; value: CampaignDetailView }[] => [
  { label: generalLabel, value: 'general' },
  { label: comparisonLabel, value: 'comparison' },
]

export const CampaignDetailHeader: FC<CampaignDetailHeaderProps> = ({
  action,
  backUrl,
  campaignName,
  className,
  compact = false,
  date,
  editUrl,
  comparisonLabel = en.comparisons,
  generalLabel = en.overview,
  isDownloading = false,
  onBack,
  onDownload,
  onViewChange,
  parent,
  showActions = true,
  view,
}) => {
  const navigate = useNavigate()

  // Campaign names carry their own path, e.g. "Snap Fitness Georgia / Brand
  // Mentions". The leading segment is the campaign itself and stays solid; the
  // trailing part is context and is dimmed, so the eye lands on the name first.
  const separator = (campaignName ?? '').match(NAME_SEPARATOR)?.[0] ?? ''
  const [primaryName, ...trailingSegments] = (campaignName ?? '').split(
    NAME_SEPARATOR
  )
  const trailingName = trailingSegments.join(separator)

  return (
    <div
      className={cn(styles.header, compact && styles.headerCompact, className)}
    >
      {parent && (
        <PageBackLink
          className={styles.parentLink}
          label={
            compact && campaignName ? `${parent} / ${campaignName}` : parent
          }
          onClick={onBack}
          to={backUrl}
        />
      )}
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          {parent ? null : onBack ? (
            <button
              aria-label={en.back}
              className={styles.backLink}
              onClick={onBack}
              type="button"
            >
              <BackIcon className={styles.backIcon} />
            </button>
          ) : (
            <Link
              aria-label={en.campaigns}
              className={styles.backLink}
              to={backUrl}
            >
              <BackIcon className={styles.backIcon} />
            </Link>
          )}

          {primaryName && !(parent && compact) && (
            <span className={styles.campaignName}>
              {primaryName}
              {trailingName && (
                <span className={styles.campaignNameTrailing}>
                  {separator}
                  {trailingName}
                </span>
              )}
            </span>
          )}

          {date && (
            <span className={styles.dateGroup}>
              <CalendarIcon className={styles.calendarIcon} />
              <span className={styles.dateText}>{date}</span>
            </span>
          )}
        </div>

        <div className={styles.viewToggle}>
          {viewOptions(generalLabel, comparisonLabel).map((option) => (
            <button
              className={cn(
                styles.viewToggleOption,
                // Figma outlines both segments once Data Comparison is the
                // active view; General Overview leaves them borderless.
                view === 'comparison' && styles.viewToggleOptionBordered,
                view === option.value && styles.viewToggleOptionActive
              )}
              key={option.value}
              onClick={() => onViewChange(option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {showActions && (
        <div className={styles.actionsRow}>
          <button
            className={styles.downloadButton}
            disabled={isDownloading}
            onClick={onDownload}
            type="button"
          >
            {isDownloading ? (
              <span className={styles.spinner} />
            ) : (
              <DownloadIcon className={styles.buttonIcon} />
            )}
            {isDownloading ? en.downloading : en.downloadMentionsReport}
          </button>

          <span className={styles.splitter} />

          {/* A button rather than a Link: as an anchor the browser previewed
              the target URL in the status bar on hover, which read as an
              unwanted hover effect and was inconsistent with the download
              button. */}
          {action ? (
            <button
              className={styles.editButton}
              onClick={action.onClick}
              type="button"
            >
              <action.icon className={styles.buttonIcon} />
              {action.label}
            </button>
          ) : (
            <button
              className={styles.editButton}
              onClick={() => editUrl && navigate(editUrl)}
              type="button"
            >
              <EditIcon className={styles.buttonIcon} />
              {en.editCampaign}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
